import type { QueryClient } from "@tanstack/react-query";
import type { LoaderFunction } from "react-router";

import { ConfigService } from "@/shared/domain/config/ConfigService";
import {
  EMPTY_TABLE_SEARCH_PREFERENCES,
  type SearchTablePreferenceKey,
  getQueryKey,
  searchTablePreferencesQuery,
} from "@/shared/domain/config/hooks/useSearchTablePreferences";
import { RobotoDomainException } from "@/shared/http/exceptions";
import { makeAuthenticatedHttpClient } from "@/shared/http/makeAuthenticatedHttpClient";
import { ErrorMonitoringService } from "@/shared/services";

/**
 * Loader function for populating table preferences. Table preferences
 * are treated as critical path data so we block on this data to avoid
 * layout shifts or additional loading mechanisms.
 *
 * Accepts a single key or an array of keys. Multiple keys are fetched in
 * parallel — use this when a route can render different table types and the
 * type isn't known until after the collection record is fetched (e.g. a
 * collection detail page, which shows a different table per member type).
 *
 * This function treats all fetch failures as "no preferences set" so the
 * user still sees the default table.
 */
export function loadSearchTablePreferences(
  queryClient: QueryClient,
  configKey: SearchTablePreferenceKey | readonly SearchTablePreferenceKey[],
): LoaderFunction {
  const keys: readonly SearchTablePreferenceKey[] =
    typeof configKey === "string" ? [configKey] : configKey;

  return async function loadTablePreferences() {
    const httpClient = await makeAuthenticatedHttpClient();
    const configService = new ConfigService(httpClient);

    await Promise.all(
      keys.map(async (key) => {
        try {
          // Populates the Tanstack Query cache with table preferences
          await queryClient.ensureQueryData(
            searchTablePreferencesQuery(configService, key),
          );
        } catch (error) {
          const isClientError =
            error instanceof RobotoDomainException && error.isClientError();

          if (!isClientError) {
            ErrorMonitoringService.captureError(error, {
              context: "searchTablePreferencesLoader",
              configKey: key,
            });
          }

          // Fetch failures still warm the cache with an empty sentinel
          queryClient.setQueryData(
            getQueryKey(key),
            EMPTY_TABLE_SEARCH_PREFERENCES,
          );
        }
      }),
    );

    return null;
  };
}
